/* -------------------------------------------------------------------------- */
/*                               CONSTANTS                                    */
/* -------------------------------------------------------------------------- */

export const MONTHS_IN_YEAR = 12;
export const PERCENT_DIVISOR = 100;

/* -------------------------------------------------------------------------- */
/*                               HELPERS                                      */
/* -------------------------------------------------------------------------- */

export function parseNumber(value) {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const parsed = Number(String(value).replace(/,/g, ""));

  return Number.isFinite(parsed) ? parsed : 0;
}

export function roundCurrency(value) {
  return Math.round((parseNumber(value) + Number.EPSILON) * 100) / 100;
}

export function formatCurrency(value, locale = "en-IN", currency = "INR") {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(parseNumber(value));
}

export function formatNumber(value, locale = "en-IN") {
  return new Intl.NumberFormat(locale).format(parseNumber(value));
}

export function formatPercent(value, decimals = 2) {
  return `${parseNumber(value).toFixed(decimals)}%`;
}

/* -------------------------------------------------------------------------- */
/*                          RATE CONVERSIONS                                  */
/* -------------------------------------------------------------------------- */

export function yearlyToMonthlyRate(rate) {
  return parseNumber(rate) / PERCENT_DIVISOR / MONTHS_IN_YEAR;
}

export function yearsToMonths(years) {
  return Math.round(parseNumber(years) * MONTHS_IN_YEAR);
}

/* -------------------------------------------------------------------------- */
/*                          EMI CALCULATIONS                                  */
/* -------------------------------------------------------------------------- */

export function calculateEMI(principal, annualRate, tenureYears) {
  principal = parseNumber(principal);

  const monthlyRate = yearlyToMonthlyRate(annualRate);

  const months = yearsToMonths(tenureYears);

  if (principal <= 0 || months <= 0) {
    return 0;
  }

  if (monthlyRate === 0) {
    return roundCurrency(principal / months);
  }

  const factor = Math.pow(1 + monthlyRate, months);

  return roundCurrency((principal * monthlyRate * factor) / (factor - 1));
}

/* -------------------------------------------------------------------------- */
/*                      LOAN SUMMARY                                          */
/* -------------------------------------------------------------------------- */

export function calculateLoanSummary({ principal, annualRate, tenureYears }) {
  principal = parseNumber(principal);

  const emi = calculateEMI(principal, annualRate, tenureYears);

  const months = yearsToMonths(tenureYears);

  const totalPayment = roundCurrency(emi * months);

  const totalInterest = roundCurrency(totalPayment - principal);

  return {
    loanAmount: principal,

    emi,

    totalInterest,

    totalPayment,

    interestPercentage:
      principal === 0 ? 0 : roundCurrency((totalInterest / principal) * 100),
  };
}

/* -------------------------------------------------------------------------- */
/*                    REVERSE EMI CALCULATOR                                  */
/* -------------------------------------------------------------------------- */

export function calculateLoanAmountFromEMI(emi, annualRate, tenureYears) {
  emi = parseNumber(emi);

  const monthlyRate = yearlyToMonthlyRate(annualRate);

  const months = yearsToMonths(tenureYears);

  if (emi <= 0 || months <= 0) {
    return 0;
  }

  if (monthlyRate === 0) {
    return roundCurrency(emi * months);
  }

  const factor = Math.pow(1 + monthlyRate, months);

  return roundCurrency((emi * (factor - 1)) / (monthlyRate * factor));
}

/* -------------------------------------------------------------------------- */
/*                     ELIGIBLE EMI CALCULATOR                                */
/* -------------------------------------------------------------------------- */

export function calculateEligibleEMI({
  monthlyIncome,
  foir,
  existingEmis = 0,
}) {
  monthlyIncome = parseNumber(monthlyIncome);
  foir = parseNumber(foir);
  existingEmis = parseNumber(existingEmis);

  const maximumEmi = roundCurrency(monthlyIncome * (foir / PERCENT_DIVISOR));

  const eligibleEmi = roundCurrency(Math.max(0, maximumEmi - existingEmis));

  return {
    monthlyIncome,

    foir,

    existingEmis,

    maximumEmi,

    eligibleEmi,
  };
}

/* -------------------------------------------------------------------------- */
/*                     LOAN ELIGIBILITY                                       */
/* -------------------------------------------------------------------------- */

export function calculateLoanEligibility({
  monthlyIncome,
  existingEmis = 0,
  foir,
  annualRate,
  tenureYears,
}) {
  const eligibility = calculateEligibleEMI({
    monthlyIncome,
    foir,
    existingEmis,
  });

  const loanAmount = calculateLoanAmountFromEMI(
    eligibility.eligibleEmi,
    annualRate,
    tenureYears,
  );

  const summary = calculateLoanSummary({
    principal: loanAmount,
    annualRate,
    tenureYears,
  });

  return {
    ...summary,

    loanAmount,

    emi: eligibility.eligibleEmi,

    eligibleEmi: eligibility.eligibleEmi,

    maximumEmi: eligibility.maximumEmi,

    monthlyIncome: eligibility.monthlyIncome,

    existingEmis: eligibility.existingEmis,

    foir: eligibility.foir,

    disposableIncome: roundCurrency(
      eligibility.monthlyIncome - eligibility.existingEmis,
    ),
  };
}

export function calculateLoanEligibilityChecker({
  monthlyIncome,
  existingEmis = 0,
  propertyValue,
  proposedLoanAmount,
  annualRate,
  tenureYears,
  allowedFoir,
  allowedLtv,
  processingFee,
}) {
  monthlyIncome = parseNumber(monthlyIncome);
  existingEmis = parseNumber(existingEmis);
  propertyValue = parseNumber(propertyValue);
  proposedLoanAmount = parseNumber(proposedLoanAmount);
  annualRate = parseNumber(annualRate);
  tenureYears = parseNumber(tenureYears);
  allowedFoir = parseNumber(allowedFoir);
  allowedLtv = parseNumber(allowedLtv);
  processingFee = parseNumber(processingFee);

  // Your calculateEMI() accepts positional arguments,
  // not an object.
  const proposedEmi = calculateEMI(proposedLoanAmount, annualRate, tenureYears);

  const maximumEmi = roundCurrency(monthlyIncome * (allowedFoir / 100));

  const eligibleEmi = roundCurrency(Math.max(0, maximumEmi - existingEmis));

  // Your calculateLoanAmountFromEMI() also accepts
  // positional arguments.
  const eligibleLoan = calculateLoanAmountFromEMI(
    eligibleEmi,
    annualRate,
    tenureYears,
  );

  const foir =
    monthlyIncome === 0
      ? 0
      : roundCurrency(((proposedEmi + existingEmis) / monthlyIncome) * 100);

  const ltv =
    propertyValue === 0
      ? 0
      : roundCurrency((proposedLoanAmount / propertyValue) * 100);

  const processingFeeAmount = roundCurrency(
    proposedLoanAmount * (processingFee / 100),
  );

  const netDisbursal = roundCurrency(proposedLoanAmount - processingFeeAmount);

  const downPayment = roundCurrency(
    Math.max(propertyValue - proposedLoanAmount, 0),
  );

  const approvalReasons = [];

  if (foir > allowedFoir) {
    approvalReasons.push("FOIR");
  }

  if (ltv > allowedLtv) {
    approvalReasons.push("LTV");
  }

  return {
    monthlyIncome,
    existingEmis,

    propertyValue,

    proposedLoanAmount,

    proposedEmi,

    maximumEmi,
    eligibleEmi,
    eligibleLoan,

    foir,
    ltv,

    allowedFoir,
    allowedLtv,

    processingFee,
    processingFeeAmount,

    netDisbursal,
    downPayment,

    approval: approvalReasons.length === 0,
    approvalReasons,
  };
}

/* -------------------------------------------------------------------------- */
/*                      MONTHLY AMORTIZATION                                  */
/* -------------------------------------------------------------------------- */

export function generateMonthlySchedule({
  principal,
  annualRate,
  tenureYears,
}) {
  principal = parseNumber(principal);

  const monthlyRate = yearlyToMonthlyRate(annualRate);

  const months = yearsToMonths(tenureYears);

  const emi = calculateEMI(principal, annualRate, tenureYears);

  if (principal <= 0 || months <= 0) {
    return [];
  }

  let balance = principal;

  const schedule = [];

  for (let month = 1; month <= months; month++) {
    const interest = roundCurrency(balance * monthlyRate);

    let principalPaid = roundCurrency(emi - interest);
    let emiPaid = emi;

    if (principalPaid > balance) {
      principalPaid = balance;
      emiPaid = roundCurrency(principalPaid + interest);
    }

    balance = roundCurrency(balance - principalPaid);

    schedule.push({
      month,

      emi: emiPaid,

      principal: principalPaid,

      interest,

      balance: Math.max(0, balance),

      cumulativePrincipal: roundCurrency(principal - balance),

      cumulativeInterest: roundCurrency(month * emi - (principal - balance)),
    });
  }

  return schedule;
}

/* -------------------------------------------------------------------------- */
/*                       YEARLY AMORTIZATION                                  */
/* -------------------------------------------------------------------------- */

export function generateAmortizationSchedule(options) {
  const monthly = generateMonthlySchedule(options);

  const yearly = [];

  for (let i = 0; i < monthly.length; i += 12) {
    const slice = monthly.slice(i, i + 12);

    yearly.push({
      year: Math.floor(i / 12) + 1,

      emi: roundCurrency(slice.reduce((sum, row) => sum + row.emi, 0)),

      principal: roundCurrency(
        slice.reduce((sum, row) => sum + row.principal, 0),
      ),

      interest: roundCurrency(
        slice.reduce((sum, row) => sum + row.interest, 0),
      ),

      balance: slice.at(-1)?.balance ?? 0,
    });
  }

  return yearly;
}

/* -------------------------------------------------------------------------- */
/*                     CHART DATA HELPERS                                     */
/* -------------------------------------------------------------------------- */

export function getLoanBreakdownData(summary) {
  return [
    {
      name: "Principal",
      value: summary.loanAmount,
    },
    {
      name: "Interest",
      value: summary.totalInterest,
    },
  ];
}

export function getOutstandingBalanceData(amortizationSchedule) {
  return amortizationSchedule.map((year) => ({
    year: `Year ${year.year}`,
    balance: year.balance,
  }));
}

export function getPrincipalInterestTrend(amortizationSchedule) {
  return amortizationSchedule.map((year) => ({
    year: `Year ${year.year}`,
    principal: year.principal,
    interest: year.interest,
  }));
}

export function formatAxisCurrency(value) {
  const number = Number(value);

  if (!Number.isFinite(number)) return "";

  if (number >= 10000000) {
    return `₹${(number / 10000000).toFixed(1)} Cr`;
  }

  if (number >= 100000) {
    return `₹${(number / 100000).toFixed(0)} L`;
  }

  if (number >= 1000) {
    return `₹${(number / 1000).toFixed(0)} K`;
  }

  return `₹${number}`;
}

export function getPrepaymentLoanBreakdownData(summary) {
  return [
    {
      name: "Principal",
      value: summary.loanAmount,
    },
    {
      name: "Interest",
      value: summary.revisedInterest,
    },
  ];
}

export function getInterestComparisonData(summary) {
  return [
    {
      name: "Original Interest",
      value: summary.originalInterest,
    },
    {
      name: "Interest Saved",
      value: summary.interestSaved,
    },
  ];
}

export function getOutstandingBalanceComparisonData(summary) {
  const original = summary.originalSchedule;
  const revised = summary.revisedSchedule;

  const maxMonths = Math.max(original.length, revised.length);

  const data = [];

  for (let i = 0; i < maxMonths; i++) {
    data.push({
      month: i + 1,

      originalBalance: original[i]?.balance ?? 0,

      revisedBalance: revised[i]?.balance ?? 0,
    });
  }

  return data;
}

export function getInterestSavedTrend(summary) {
  const original = summary.originalSchedule;
  const revised = summary.revisedSchedule;

  const maxMonths = Math.max(original.length, revised.length);

  const data = [];

  let originalInterest = 0;
  let revisedInterest = 0;

  for (let i = 0; i < maxMonths; i++) {
    originalInterest += original[i]?.interest ?? 0;
    revisedInterest += revised[i]?.interest ?? 0;

    data.push({
      month: i + 1,

      interestSaved: roundCurrency(originalInterest - revisedInterest),
    });
  }

  return data;
}

export function getPrincipalComparisonData(summary) {
  const original = summary.originalSchedule;
  const revised = summary.revisedSchedule;

  const maxMonths = Math.max(original.length, revised.length);

  const data = [];

  let originalPrincipal = 0;
  let revisedPrincipal = 0;

  for (let i = 0; i < maxMonths; i++) {
    originalPrincipal += original[i]?.principal ?? 0;

    revisedPrincipal +=
      (revised[i]?.principal ?? 0) + (revised[i]?.prepayment ?? 0);

    data.push({
      month: i + 1,

      originalPrincipal: roundCurrency(originalPrincipal),

      revisedPrincipal: roundCurrency(revisedPrincipal),
    });
  }

  return data;
}

export function getUpfrontCostBreakdownData(summary) {
  return [
    {
      name: "Down Payment",
      value: summary.downPayment,
    },
    {
      name: "Stamp Duty",
      value: summary.stampDuty,
    },
    {
      name: "Registration",
      value: summary.registration,
    },
  ];
}

/* -------------------------------------------------------------------------- */
/*                           DATE HELPERS                                     */
/* -------------------------------------------------------------------------- */

export function addMonths(date, months) {
  const next = new Date(date);

  next.setMonth(next.getMonth() + months);

  return next;
}

export function formatMonthYear(date, locale = "en-IN") {
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    year: "numeric",
  }).format(date);
}

export function formatDate(date, locale = "en-IN") {
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

/* -------------------------------------------------------------------------- */
/*                       PAYMENT CALENDAR                                     */
/* -------------------------------------------------------------------------- */

export function generatePaymentCalendar({
  principal,
  annualRate,
  tenureYears,
  startDate = new Date(),
}) {
  const monthlySchedule = generateMonthlySchedule({
    principal,
    annualRate,
    tenureYears,
  });

  return monthlySchedule.map((payment) => {
    const paymentDate = addMonths(startDate, payment.month - 1);

    return {
      ...payment,

      paymentDate,

      paymentLabel: formatMonthYear(paymentDate),

      paymentDateFormatted: formatDate(paymentDate),
    };
  });
}

/* -------------------------------------------------------------------------- */
/*                     YEARLY PAYMENT CALENDAR                                */
/* -------------------------------------------------------------------------- */

export function generateYearlyCalendar(options) {
  const payments = generatePaymentCalendar(options);

  const years = [];

  for (let i = 0; i < payments.length; i += 12) {
    const slice = payments.slice(i, i + 12);

    years.push({
      year: Math.floor(i / 12) + 1,

      startDate: slice[0]?.paymentDate,

      endDate: slice.at(-1)?.paymentDate,

      startLabel: slice[0]?.paymentLabel,

      endLabel: slice.at(-1)?.paymentLabel,

      emi: roundCurrency(slice.reduce((sum, row) => sum + row.emi, 0)),

      principal: roundCurrency(
        slice.reduce((sum, row) => sum + row.principal, 0),
      ),

      interest: roundCurrency(
        slice.reduce((sum, row) => sum + row.interest, 0),
      ),

      balance: slice.at(-1)?.balance ?? 0,
    });
  }

  return years;
}

/* -------------------------------------------------------------------------- */
/*                     LOAN MILESTONES                                        */
/* -------------------------------------------------------------------------- */

export function getLoanMilestones(options) {
  const payments = generatePaymentCalendar(options);

  if (!payments.length) {
    return null;
  }

  return {
    firstPayment: payments[0],

    halfwayPayment: payments[Math.floor(payments.length / 2)],

    finalPayment: payments.at(-1),
  };
}

/* -------------------------------------------------------------------------- */
/*                    EMI Capacity Breakdown                                       */
/* -------------------------------------------------------------------------- */

export function getEmiCapacityData(summary) {
  const { existingEmis, eligibleEmi, maximumEmi } = summary;

  const unusedCapacity = Math.max(0, maximumEmi - existingEmis - eligibleEmi);

  return [
    {
      name: "Existing EMIs",
      value: existingEmis,
    },
    {
      name: "New Loan EMI",
      value: eligibleEmi,
    },
    {
      name: "Unused Capacity",
      value: unusedCapacity,
    },
  ];
}

/* -------------------------------------------------------------------------- */
/*                    Property Financing Breakdown                                     */
/* -------------------------------------------------------------------------- */

export function getPropertyFinancingData(summary) {
  const isEligibleLoan = summary.loanEligible !== undefined;

  return [
    {
      name: isEligibleLoan ? "Loan Eligible" : "Loan Amount",
      value:
        summary.proposedLoanAmount ??
        summary.loanEligible ??
        summary.loanAmount ??
        0,
    },
    {
      name: "Down Payment",
      value: summary.downPayment,
    },
  ];
}

/* -------------------------------------------------------------------------- */
/*                    Loan Prepayment                                     */
/* -------------------------------------------------------------------------- */

function simulateLoan({
  principal,
  annualRate,
  tenureYears,
  emi,
  prepaymentStrategy,
  keepEmiConstant = true,
}) {
  principal = parseNumber(principal);

  annualRate = parseNumber(annualRate);

  tenureYears = parseNumber(tenureYears);

  emi = emi ?? calculateEMI(principal, annualRate, tenureYears);

  const monthlyRate = yearlyToMonthlyRate(annualRate);

  const totalMonths = yearsToMonths(tenureYears);

  const schedule = [];

  let balance = principal;

  let currentEmi = emi;

  const originalEmi = emi;

  let revisedEmi = emi;

  let totalInterest = 0;

  let totalPrincipal = 0;

  let totalPrepayment = 0;

  let month = 0;

  while (balance > 0.01 && month < yearsToMonths(50)) {
    month++;

    const openingBalance = balance;

    const interest = roundCurrency(balance * monthlyRate);

    let principalPaid = roundCurrency(currentEmi - interest);

    if (principalPaid > balance) {
      principalPaid = balance;

      currentEmi = roundCurrency(principalPaid + interest);
    }

    balance = roundCurrency(balance - principalPaid);

    totalInterest += interest;

    totalPrincipal += principalPaid;

    let prepayment = 0;

    if (typeof prepaymentStrategy === "function") {
      prepayment = roundCurrency(
        prepaymentStrategy({
          month,
          balance,
        }),
      );
    }

    prepayment = Math.min(prepayment, balance);

    if (prepayment > 0) {
      balance = roundCurrency(balance - prepayment);

      totalPrepayment += prepayment;

      if (!keepEmiConstant && balance > 0) {
        const remainingMonths = Math.max(totalMonths - month, 1);

        revisedEmi = calculateEMI(balance, annualRate, remainingMonths / 12);

        currentEmi = revisedEmi;
      }
    }

    schedule.push({
      month,

      openingBalance,

      emi: currentEmi,

      principal: principalPaid,

      interest,

      prepayment,

      totalPrincipalPaid: roundCurrency(principalPaid + prepayment),

      balance: roundCurrency(Math.max(balance, 0)),
    });
  }

  return {
    schedule,

    months: schedule.length,

    emi: roundCurrency(keepEmiConstant ? originalEmi : revisedEmi),

    outstandingBalance: balance,

    totalInterest: roundCurrency(totalInterest),

    totalPrincipal: roundCurrency(totalPrincipal),

    totalPrepayment: roundCurrency(totalPrepayment),

    totalPayment: roundCurrency(
      totalInterest + totalPrincipal + totalPrepayment,
    ),

    loanClosed: balance <= 0.01,
  };
}

export function calculateLoanPrepayment({
  loanAmount,
  annualRate,
  tenureYears,

  prepaymentType = "oneTime",
  prepaymentAmount,
  startMonth,

  keepEmiConstant = true,
}) {
  loanAmount = parseNumber(loanAmount);
  annualRate = parseNumber(annualRate);
  tenureYears = parseNumber(tenureYears);
  prepaymentAmount = parseNumber(prepaymentAmount);
  startMonth = parseNumber(startMonth);

  const originalSummary = calculateLoanSummary({
    principal: loanAmount,
    annualRate,
    tenureYears,
  });

  const simulation = simulateLoan({
    principal: loanAmount,
    annualRate,
    tenureYears,

    emi: originalSummary.emi,

    keepEmiConstant,

    prepaymentStrategy: ({ month, balance }) =>
      getPrepaymentForMonth({
        month,
        balance,
        prepaymentType,
        prepaymentAmount,
        startMonth,
      }),
  });

  const interestSaved = roundCurrency(
    originalSummary.totalInterest - simulation.totalInterest,
  );

  const monthsSaved = yearsToMonths(tenureYears) - simulation.months;

  const yearsSaved = Math.floor(monthsSaved / 12);

  const remainingMonthsSaved = monthsSaved % 12;

  const timeSavedText =
    yearsSaved > 0
      ? `${yearsSaved} year${yearsSaved > 1 ? "s" : ""}${
          remainingMonthsSaved
            ? ` ${remainingMonthsSaved} month${
                remainingMonthsSaved > 1 ? "s" : ""
              }`
            : ""
        }`
      : `${remainingMonthsSaved} month${remainingMonthsSaved > 1 ? "s" : ""}`;

  const interestSavingPercentage =
    originalSummary.totalInterest > 0
      ? roundCurrency((interestSaved / originalSummary.totalInterest) * 100)
      : 0;

  const savingsEfficiency =
    simulation.totalPrepayment > 0
      ? roundCurrency((interestSaved / simulation.totalPrepayment) * 100)
      : 0;

  return {
    // Original Loan
    loanAmount,

    originalLoanAmount: loanAmount,

    originalEmi: originalSummary.emi,

    originalInterest: originalSummary.totalInterest,

    originalTotalPayment: originalSummary.totalPayment,

    originalTenureMonths: yearsToMonths(tenureYears),

    // Revised Loan
    revisedEmi: simulation.emi,

    revisedInterest: simulation.totalInterest,

    revisedTotalPayment: simulation.totalPayment,

    revisedTenureMonths: simulation.months,

    // Savings
    interestSaved,

    interestSavingPercentage,

    monthsSaved,

    yearsSaved,

    remainingMonthsSaved,

    timeSavedText,

    savingsEfficiency,

    totalPrepayment: simulation.totalPrepayment,

    // Schedules
    originalSchedule: generateMonthlySchedule({
      principal: loanAmount,
      annualRate,
      tenureYears,
    }),

    revisedSchedule: simulation.schedule,

    // Configuration
    prepaymentType,

    prepaymentAmount,

    startMonth,

    keepEmiConstant,
  };
}

function getPrepaymentForMonth({
  month,
  balance,
  prepaymentType,
  prepaymentAmount,
  startMonth,
}) {
  if (balance <= 0 || prepaymentAmount <= 0) {
    return 0;
  }

  switch (prepaymentType) {
    case "oneTime":
      return month === startMonth ? Math.min(prepaymentAmount, balance) : 0;

    case "monthly":
      return month >= startMonth ? Math.min(prepaymentAmount, balance) : 0;

    case "quarterly":
      return month >= startMonth && (month - startMonth) % 3 === 0
        ? Math.min(prepaymentAmount, balance)
        : 0;

    case "yearly":
      return month >= startMonth && (month - startMonth) % 12 === 0
        ? Math.min(prepaymentAmount, balance)
        : 0;

    default:
      return 0;
  }
}

/* -------------------------------------------------------------------------- */
/*                     BALANCE TRANSFER CHART HELPERS                          */
/* -------------------------------------------------------------------------- */

export function getBalanceTransferInterestComparisonData(summary) {
  return [
    {
      name: "Interest Saved",
      value: summary.interestSaved,
    },
    {
      name: "Interest Payable",
      value: summary.newInterest,
    },
  ];
}

export function getEmiComparisonData(summary) {
  return [
    {
      name: "Current EMI",
      value: summary.currentEmi,
    },
    {
      name: "New EMI",
      value: summary.newEmi,
    },
  ];
}

export function getTransferCostBreakdownData(summary) {
  return [
    {
      name: "Processing Fee",
      value: summary.processingFeeAmount,
    },
    {
      name: "Legal Charges",
      value: summary.legalCharges,
    },
    {
      name: "Other Charges",
      value: summary.otherCharges,
    },
  ];
}

export function getNetSavingsBreakdownData(summary) {
  return [
    {
      name: "Transfer Cost",
      value: summary.transferCost,
    },
    {
      name: "Net Savings",
      value: Math.max(summary.netSavings, 0),
    },
  ];
}

export function getBalanceTransferOutstandingComparisonData(summary) {
  const currentSchedule = summary.currentSchedule ?? [];
  const newSchedule = summary.newSchedule ?? [];

  const maxMonths = Math.max(currentSchedule.length, newSchedule.length);

  return Array.from({ length: maxMonths }, (_, index) => ({
    month: index + 1,

    currentBalance: currentSchedule[index]?.balance ?? 0,

    transferredBalance: newSchedule[index]?.balance ?? 0,
  }));
}

export function getMonthlySavingsTrend(summary) {
  const currentSchedule = summary.currentSchedule ?? [];
  const newSchedule = summary.newSchedule ?? [];

  const maxMonths = Math.max(currentSchedule.length, newSchedule.length);

  let cumulativeCurrentInterest = 0;
  let cumulativeNewInterest = 0;

  const data = [];

  for (let i = 0; i < maxMonths; i++) {
    cumulativeCurrentInterest += currentSchedule[i]?.interest ?? 0;

    cumulativeNewInterest += newSchedule[i]?.interest ?? 0;

    data.push({
      month: i + 1,

      savings: cumulativeCurrentInterest - cumulativeNewInterest,
    });
  }

  return data;
}

/* -------------------------------------------------------------------------- */
/*                     TAX BENEFITS                          */
/* -------------------------------------------------------------------------- */

export function getOldRegimeMarginalRate(income) {
  if (income <= 250000) return 0;
  if (income <= 500000) return 0.05;
  if (income <= 1000000) return 0.2;
  return 0.3;
}

export function getNewRegimeMarginalRate(income) {
  if (income <= 400000) return 0;
  if (income <= 800000) return 0.05;
  if (income <= 1200000) return 0.1;
  if (income <= 1600000) return 0.15;
  if (income <= 2000000) return 0.2;
  if (income <= 2400000) return 0.25;
  return 0.3;
}

/**
 * Returns the principal and interest paid during a specific repayment year.
 *
 * @param {Array} monthlySchedule Monthly amortization schedule
 * @param {number} repaymentYear 1-based repayment year
 * @returns {{
 *   principal: number,
 *   interest: number,
 *   emi: number,
 *   totalPayment: number
 * }}
 */
export function getLoanYearBreakdown(monthlySchedule, repaymentYear) {
  const startIndex = Math.max(0, (repaymentYear - 1) * 12);

  const yearSchedule = monthlySchedule.slice(startIndex, startIndex + 12);

  return yearSchedule.reduce(
    (result, month) => {
      result.principal += month.principal;
      result.interest += month.interest;
      result.emi += month.emi;
      result.totalPayment += month.emi;

      return result;
    },
    {
      principal: 0,
      interest: 0,
      emi: 0,
      totalPayment: 0,
    },
  );
}

/**
 * Converts an object into chart data for pie/bar charts.
 *
 * Example:
 * getBreakdownChartData({
 *   Principal: 120000,
 *   Interest: 350000,
 * })
 *
 * =>
 * [
 *   { name: "Principal", value: 120000 },
 *   { name: "Interest", value: 350000 }
 * ]
 */
export function getBreakdownChartData(
  items,
  { nameKey = "name", valueKey = "value", removeZeroValues = true } = {},
) {
  return Object.entries(items)
    .filter(([, value]) => (removeZeroValues ? Number(value) > 0 : true))
    .map(([label, value]) => ({
      [nameKey]: label,
      [valueKey]: Number(value),
    }));
}

/* -------------------------------------------------------------------------- */
/*                     DETAILED FOIR CALCULATOR                               */
/* -------------------------------------------------------------------------- */

const HOME_LOAN_FOIR_SLABS = {
  salaried: [
    { max: 30000, pct: 60 },
    { max: 40000, pct: 65 },
    { max: Infinity, pct: 75 },
  ],

  sep: [
    { max: 30000, pct: 65 },
    { max: Infinity, pct: 70 },
  ],

  senp: [
    { max: 30000, pct: 65 },
    { max: Infinity, pct: 70 },
  ],
};

const PLOT_LOAN_FOIR_SLABS = {
  salaried: {
    metro: [
      { max: 30000, pct: 50 },
      { max: 50000, pct: 55 },
      { max: Infinity, pct: 65 },
    ],

    nonmetro: [
      { max: 30000, pct: 55 },
      { max: 50000, pct: 60 },
      { max: Infinity, pct: 70 },
    ],
  },

  sep: [
    { max: 30000, pct: 65 },
    { max: 50000, pct: 70 },
    { max: Infinity, pct: 70 },
  ],

  senp: [
    { max: 30000, pct: 60 },
    { max: 50000, pct: 65 },
    { max: Infinity, pct: 65 },
  ],
};

export const DETAILED_FOIR_MINIMUMS = {
  salariedMonthlyIncome: 25000,
  selfEmployedAnnualIncome: 350000,
};

export function getAverageIncome(values) {
  const category = values.category;

  if (category === "salaried") {
    const month1 = parseNumber(values.month1);
    const month2 = parseNumber(values.month2);
    const month3 = parseNumber(values.month3);

    const averageMonthlyIncome = roundCurrency(
      (month1 + month2 + month3) / 3,
    );

    return {
      averageMonthlyIncome,

      averageAnnualIncome: roundCurrency(
        averageMonthlyIncome * MONTHS_IN_YEAR,
      ),
    };
  }

  const fy1 = parseNumber(values.fy1);
  const fy2 = parseNumber(values.fy2);

  const averageAnnualIncome = roundCurrency((fy1 + fy2) / 2);

  return {
    averageAnnualIncome,

    averageMonthlyIncome: roundCurrency(
      averageAnnualIncome / MONTHS_IN_YEAR,
    ),
  };
}

export function getTotalObligations(obligations = []) {
  return roundCurrency(
    obligations.reduce(
      (total, obligation) =>
        total + parseNumber(obligation.amount),
      0,
    ),
  );
}

export function getAllowedFoirPercentage({
  category,
  loanType,
  location = "metro",
  averageMonthlyIncome,
}) {
  const monthlyIncome = parseNumber(averageMonthlyIncome);

  let slabs;

  if (loanType === "home") {
    slabs = HOME_LOAN_FOIR_SLABS[category];
  } else {
    slabs =
      category === "salaried"
        ? PLOT_LOAN_FOIR_SLABS.salaried[location]
        : PLOT_LOAN_FOIR_SLABS[category];
  }

  const slab =
    slabs.find(({ max }) => monthlyIncome <= max) ??
    slabs.at(-1);

  return slab.pct;
}

export function getEligibilityStatus({
  category,
  averageMonthlyIncome,
  averageAnnualIncome,
  proposedEmi,
  eligibleEmi,
}) {
  let minimumEligible = true;
  let minimumReason = "";

  if (category === "salaried") {
    minimumEligible =
      averageMonthlyIncome >=
      DETAILED_FOIR_MINIMUMS.salariedMonthlyIncome;

    if (!minimumEligible) {
      minimumReason =
        "Average monthly income is below ₹25,000.";
    }
  } else {
    minimumEligible =
      averageAnnualIncome >=
      DETAILED_FOIR_MINIMUMS.selfEmployedAnnualIncome;

    if (!minimumEligible) {
      minimumReason =
        "Average annual income is below ₹3.5 lakh.";
    }
  }

  const headroom = roundCurrency(
    eligibleEmi - proposedEmi,
  );

  let status;

  if (!minimumEligible) {
    status = "NOT_ELIGIBLE";
  } else if (headroom >= 0) {
    status = "FOIR_CLEARED";
  } else {
    status = "EXCEEDS_FOIR";
  }

  return {
    status,

    approved:
      minimumEligible && headroom >= 0,

    minimumEligible,

    minimumReason,

    headroom,

    shortfall: Math.abs(
      Math.min(headroom, 0),
    ),
  };
}

/* -------------------------------------------------------------------------- */
/*                     DETAILED FOIR CHART HELPERS                            */
/* -------------------------------------------------------------------------- */

export function getIncomeBreakdownData({
  averageMonthlyIncome,
  totalObligations,
  eligibleEmi,
}) {
  const data = [
    {
      name: "Existing EMIs",
      value: roundCurrency(totalObligations),
    },
    {
      name: "Available EMI",
      value: roundCurrency(eligibleEmi),
    },
  ];

  const unusedIncome = Math.max(
    0,
    roundCurrency(
      averageMonthlyIncome -
        totalObligations -
        eligibleEmi,
    ),
  );

  if (unusedIncome > 0) {
    data.push({
      name: "Unused Income",
      value: unusedIncome,
    });
  }

  return data;
}

export function getObligationBreakdownData(
  obligations = [],
) {
  if (!obligations.length) {
    return [
      {
        name: "No Existing Obligations",
        value: 1,
      },
    ];
  }

  return obligations.map((item, index) => ({
    name:
      item.type ??
      `Obligation ${index + 1}`,

    value: roundCurrency(
      parseNumber(item.amount),
    ),
  }));
}

export function getFoirBreakdownData({
  averageMonthlyIncome,
  proposedEmi,
  totalObligations,
}) {
  const remainingIncome = Math.max(
    0,
    roundCurrency(
      averageMonthlyIncome -
        proposedEmi -
        totalObligations,
    ),
  );

  return [
    {
      name: "Proposed EMI",
      value: roundCurrency(proposedEmi),
    },
    {
      name: "Existing EMIs",
      value: roundCurrency(totalObligations),
    },
    {
      name: "Remaining Income",
      value: remainingIncome,
    },
  ];
}

export function getEligibilityComparisonData({
  eligibleEmi,
  proposedEmi,
}) {
  return [
    {
      label: "Eligible EMI",
      amount: roundCurrency(eligibleEmi),
    },
    {
      label: "Proposed EMI",
      amount: roundCurrency(proposedEmi),
    },
  ];
}
